<template>
	<div class="testimonials-carousel">
		<div class="testimonial-container">
			<!-- Hidden testimonials for height measurement -->
			<div class="testimonial-measurement">
				<div
					v-for="(testimonial, index) in testimonials"
					:key="index"
					:ref="el => (testimonialRefs[index] = el)"
					class="testimonial-measure-item"
				>
					<div class="testimonial-quote-icon">
						<i class="bi bi-quote"></i>
					</div>
					<div class="testimonial-text">
						<p>{{ testimonial.testimonial }}</p>
					</div>
					<div class="testimonial-author">
						<strong>{{ testimonial.author }}</strong>
						<span v-if="testimonial.position" class="testimonial-position">
							{{ testimonial.position }}
						</span>
					</div>
				</div>
			</div>

			<!-- Current Testimonial Display -->
			<div class="testimonial-display" :style="{ height: displayHeight + 'px' }">
				<div class="testimonial-quote-icon">
					<i class="bi bi-quote"></i>
				</div>
				<div class="testimonial-text">
					<p>{{ currentTestimonial.testimonial }}</p>
				</div>
				<div class="testimonial-author">
					<strong>{{ currentTestimonial.author }}</strong>
					<span v-if="currentTestimonial.position" class="testimonial-position">
						{{ currentTestimonial.position }}
					</span>
				</div>
			</div>

			<!-- Navigation Controls -->
			<div class="testimonial-controls">
				<button
					class="testimonial-nav-btn testimonial-prev"
					@click="previousTestimonial"
					:disabled="currentIndex === 0"
					aria-label="Previous testimonial"
				>
					<i class="bi bi-chevron-left"></i>
				</button>

				<!-- Dot Indicators -->
				<div class="testimonial-indicators">
					<button
						v-for="(testimonial, index) in testimonials"
						:key="index"
						class="testimonial-dot"
						:class="{ active: index === currentIndex }"
						@click="goToTestimonial(index)"
						:aria-label="`Go to testimonial ${index + 1}`"
					></button>
				</div>

				<button
					class="testimonial-nav-btn testimonial-next"
					@click="nextTestimonial"
					:disabled="currentIndex === testimonials.length - 1"
					aria-label="Next testimonial"
				>
					<i class="bi bi-chevron-right"></i>
				</button>
			</div>

			<!-- Counter -->
			<div class="testimonial-counter">
				{{ currentIndex + 1 }} / {{ testimonials.length }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
	testimonials: {
		type: Array,
		required: true,
	},
});

const currentIndex = ref(0);
const displayHeight = ref(300); // Default fallback
const testimonialRefs = ref([]);
let resizeTimeout = null;

const currentTestimonial = computed(() => {
	return props.testimonials[currentIndex.value] || {};
});

const nextTestimonial = () => {
	if (currentIndex.value < props.testimonials.length - 1) {
		currentIndex.value++;
	}
};

const previousTestimonial = () => {
	if (currentIndex.value > 0) {
		currentIndex.value--;
	}
};

const goToTestimonial = index => {
	currentIndex.value = index;
};

// Calculate the tallest testimonial height
const calculateMaxHeight = () => {
	nextTick(() => {
		let maxHeight = 300; // Minimum height

		testimonialRefs.value.forEach(el => {
			if (el) {
				const height = el.offsetHeight;
				if (height > maxHeight) {
					maxHeight = height;
				}
			}
		});

		displayHeight.value = maxHeight;
	});
};

// Throttled resize handler
const handleResize = () => {
	if (resizeTimeout) {
		clearTimeout(resizeTimeout);
	}

	resizeTimeout = setTimeout(() => {
		calculateMaxHeight();
	}, 150); // 150ms throttle
};

onMounted(() => {
	calculateMaxHeight();
	window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
	window.removeEventListener('resize', handleResize);
	if (resizeTimeout) {
		clearTimeout(resizeTimeout);
	}
});
</script>

<style scoped>
.testimonials-carousel {
	max-width: 900px;
	margin: 0 auto;
	padding: 2rem 1rem;
}

.testimonial-container {
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	padding: 3rem 2rem 2rem;
	position: relative;
}

/* Hidden container for measuring testimonial heights */
.testimonial-measurement {
	position: absolute;
	visibility: hidden;
	pointer-events: none;
	left: 0;
	top: 0;
	width: 100%;
	padding: 3rem 2rem 2rem;
}

.testimonial-measure-item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	max-width: 900px;
	margin: 0 auto;
}

.testimonial-display {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	transition: height 0.3s ease;
}

.testimonial-quote-icon {
	font-size: 4rem;
	color: #003366;
	opacity: 0.2;
	margin-bottom: 1rem;
}

.testimonial-text {
	font-size: 1.25rem;
	line-height: 1.8;
	color: #333;
	margin-bottom: 2rem;
	font-style: italic;
	max-width: 700px;
}

.testimonial-text p {
	margin: 0;
}

.testimonial-author {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	font-size: 1rem;
}

.testimonial-author strong {
	font-size: 1.125rem;
	color: #003366;
}

.testimonial-position {
	color: #666;
	font-size: 0.95rem;
}

.testimonial-controls {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 2rem;
	margin-top: 2rem;
	padding-top: 2rem;
	border-top: 1px solid #e0e0e0;
}

.testimonial-nav-btn {
	background: #003366;
	color: #fff;
	border: none;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	font-size: 1.5rem;
}

.testimonial-nav-btn:hover:not(:disabled) {
	background: #004488;
	transform: scale(1.05);
}

.testimonial-nav-btn:disabled {
	background: #ccc;
	cursor: not-allowed;
	opacity: 0.5;
}

.testimonial-indicators {
	display: flex;
	gap: 0.75rem;
	align-items: center;
}

.testimonial-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #d0d0d0;
	border: none;
	cursor: pointer;
	padding: 0;
	transition: all 0.3s ease;
}

.testimonial-dot:hover {
	background: #999;
	transform: scale(1.2);
}

.testimonial-dot.active {
	background: #003366;
	width: 16px;
	height: 16px;
}

.testimonial-counter {
	text-align: center;
	margin-top: 1rem;
	color: #666;
	font-size: 0.9rem;
	font-weight: 500;
}

/* Responsive Styles */
@media (max-width: 768px) {
	.testimonial-container,
	.testimonial-measurement {
		padding: 2rem 1.5rem 1.5rem;
	}

	.testimonial-text {
		font-size: 1.1rem;
	}

	.testimonial-controls {
		gap: 1rem;
	}

	.testimonial-nav-btn {
		width: 40px;
		height: 40px;
		font-size: 1.25rem;
	}
}

@media (max-width: 480px) {
	.testimonials-carousel {
		padding: 1rem 0.5rem;
	}

	.testimonial-container,
	.testimonial-measurement {
		padding: 1.5rem 1rem 1rem;
	}

	.testimonial-text {
		font-size: 1rem;
	}

	.testimonial-quote-icon {
		font-size: 3rem;
	}
}
</style>
